import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import FollowingFeedWithSortByStorageSnippetsQuery, {
	type FollowingFeedWithSortByStorageSnippetsQuery$data,
	type FollowingFeedWithSortByStorageSnippetsQuery$variables,
} from './__generated__/FollowingFeedWithSortByStorageSnippetsQuery.graphql.ts';
import PopularFeedWithTimeGranularityStorageSnippetsQuery, {
	type PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
	type PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsQuery.graphql.ts';
import useFollowingFeedWithSortByStorageFeedContentQuery, {
	type useFollowingFeedWithSortByStorageFeedContentQuery$data,
	type useFollowingFeedWithSortByStorageFeedContentQuery$variables,
} from './__generated__/useFollowingFeedWithSortByStorageFeedContentQuery.graphql.ts';
import useFeedTypeSelectorRefreshQuery, {
	type useFeedTypeSelectorRefreshQuery$data,
	type useFeedTypeSelectorRefreshQuery$variables,
} from './__generated__/useFeedTypeSelectorRefreshQuery.graphql.ts';
import usePopularFeedWithTimeGranularityStorageQuery, {
	type usePopularFeedWithTimeGranularityStorageQuery$data,
	type usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';
import PopularFeedWithTimeGranularityStorageSnippetsV2Query, {
	type PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
	type PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsV2Query.graphql.ts';

export const preloadFeed = ({
	isLoggedIn,
}: {
	isLoggedIn: any;
}): Promise<void | any[] | Error[]> => {
	// If the user is not logged in we show the following feed by default
	// so only preload that feed. If the user is logged in preload both
	// feeds and the feed preference

	const { isRovoLLMEnabled } = getPreloaderFnContext();

	const followingFeedQuery = UNSAFE_expValEqualsNoExposure(
		'confluence_ai_snippets_in_following_popular_feeds',
		'isEnabled',
		true,
	)
		? queryAgg<
				FollowingFeedWithSortByStorageSnippetsQuery$data,
				FollowingFeedWithSortByStorageSnippetsQuery$variables
			>({
				query: FollowingFeedWithSortByStorageSnippetsQuery,
				variables: {
					first: expVal('confluence_home_feed_initial_page_size', 'following_ssr', 5),
				},
			})
		: query<
				useFollowingFeedWithSortByStorageFeedContentQuery$data,
				useFollowingFeedWithSortByStorageFeedContentQuery$variables
			>({
				query: useFollowingFeedWithSortByStorageFeedContentQuery,
				variables: {
					first: expVal('confluence_home_feed_initial_page_size', 'following_ssr', 5),
				},
			});

	// Select the popular-feed query to preload. Decision order MUST mirror
	// `usePopularFeedWithTimeGranularityStorage`'s client-side selection so the
	// SSR-cached response matches the query the client fires on hydration.
	// Otherwise SSR users would suffer a cache miss + re-fetch, negating the
	// latency win for the very users we care most about (first paint).
	//
	// The V2 experiment in Statsig (`confluence_ai_snippets_popular_feed`) is
	// configured with an `isEnabled` boolean param (NOT a `cohort` string param).
	// All branch checks use `('isEnabled', true)` to detect the treatment arm.
	//
	//   - V2 (parallel)            when popular_feed isEnabled=true AND isRovoLLMEnabled
	//   - V1-with-snippets         when in_following_popular_feeds isEnabled=true AND NOT V2-treatment
	//   - vanilla V1               otherwise (incl. non-Rovo V2-treatment users)
	const popularFirstCount = fg('confluence_ai_feeds_home_banner')
		? 10
		: expVal('confluence_home_feed_initial_page_size', 'popular_ssr', 5);

	let popularFeedDataQuery;
	if (
		isRovoLLMEnabled &&
		UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true)
	) {
		// V2 (parallel hydration) — node-level aiProperty, AGG calls cc-smarts
		// in parallel with the Confluence content hop. We only enter this
		// branch when `isRovoLLMEnabled` is true, so the V2 query variable can
		// be passed `true` unconditionally — no need to re-evaluate the
		// prereq experiment here.
		popularFeedDataQuery = queryAgg<
			PopularFeedWithTimeGranularityStorageSnippetsV2Query$data,
			PopularFeedWithTimeGranularityStorageSnippetsV2Query$variables
		>({
			query: PopularFeedWithTimeGranularityStorageSnippetsV2Query,
			variables: {
				first: popularFirstCount,
				isRovoLLMEnabled: true,
			},
		});
	} else if (
		// V1-with-snippets is suppressed for ANY user in the V2-treatment arm
		// (Rovo or not), matching the client-side hook's skipQuery condition.
		// Without this guard, non-Rovo users with popular_feed isEnabled=true
		// would receive a V1-with-snippets SSR preload while the client fires
		// vanilla V1, causing an SSR cache miss + re-fetch (negating the
		// latency win for the very users we care most about — first paint).
		//
		// Gate order: V2-treatment exclusion FIRST so the in-following gate
		// stays at the end of the expression and satisfies the
		// `confluence-feature-gating/no-preconditioning` ESLint rule.
		!UNSAFE_expValEqualsNoExposure('confluence_ai_snippets_popular_feed', 'isEnabled', true) &&
		UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
	) {
		// V1-with-snippets (legacy serial path). Use the NO-exposure variant
		// for the prereq experiment check — SSR fires before the user sees the
		// page, so we must NOT log a real exposure here. The client-side hook
		// (usePopularFeedWithTimeGranularityStorage) uses `expVal` for the
		// same check and is the single source of truth for exposure logging —
		// SSR and client both return the same `isRovoLLMEnabled` variable
		// value, ensuring the SSR-cached response matches what the client
		// would have fetched. The intentional divergence is exposure logging.
		// Semantics preserved from the original `expVal('...', 'isEnabled', false)`
		// which returned the boolean `isEnabled` value: when the gate is ON
		// (isEnabled=true) honor the user's real Rovo flag, otherwise pass
		// `true` (legacy pre-prereq behaviour).
		popularFeedDataQuery = queryAgg<
			PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
			PopularFeedWithTimeGranularityStorageSnippetsQuery$variables
		>({
			query: PopularFeedWithTimeGranularityStorageSnippetsQuery,
			variables: {
				first: popularFirstCount,
				isRovoLLMEnabled: UNSAFE_expValEqualsNoExposure(
					'confluence_ai_snippets_popular_feed_for_ai_enabled',
					'isEnabled',
					true,
				)
					? isRovoLLMEnabled
					: true,
			},
		});
	} else {
		// Vanilla V1 (no AI snippets). Used by: users outside both gates, and
		// non-Rovo users in the V2-treatment arm (V2 cannot serve them, and
		// V1-with-snippets is suppressed for V2-treatment users — vanilla V1
		// is the safe fallback).
		popularFeedDataQuery = query<
			usePopularFeedWithTimeGranularityStorageQuery$data,
			usePopularFeedWithTimeGranularityStorageQuery$variables
		>({
			query: usePopularFeedWithTimeGranularityStorageQuery,
			variables: {
				first: popularFirstCount,
			},
		});
	}

	const popularFeedQuery = [
		popularFeedDataQuery,
		query<useFeedTypeSelectorRefreshQuery$data, useFeedTypeSelectorRefreshQuery$variables>({
			query: useFeedTypeSelectorRefreshQuery,
		}),
	];
	const tasks: Array<Promise<any>> = [followingFeedQuery];
	if (isLoggedIn) {
		tasks.push(...popularFeedQuery);
	}
	return waitForAllReturnWhenError(tasks);
};
